import classNames from 'classnames';
import { AsYouTypeFormatter } from 'google-libphonenumber';
import React, { Fragment } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { makeStyles } from '@mui/styles';

import { Enum, PhoneType } from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { MemberPhone, Phone } from '@vestahealthcare/common/models';
import { parse, validate } from '@vestahealthcare/common/utils/phone';

import {
  Colors,
  IconButton,
  RadioGroup,
  Select,
  TextInput,
} from 'styleguide-v2';

type MemberPhoneOptional = Omit<MemberPhone, 'phone'> & {
  phone: Partial<Phone>;
};

const useStyles = makeStyles({
  addIcon: {
    marginTop: '-0.25rem',
  },
  addLink: {
    '&&': {
      cursor: 'pointer',
    },

    color: Colors.textGreen,
    fontSize: '0.875em',
    marginLeft: '0.5rem',
    textDecoration: 'underline',

    '&:hover': {
      color: Colors.iconGreen,
    },
  },
  container: {
    height: 'fit-content',
  },
  firstRadio: {
    height: '7rem',
  },
  radio: {
    '&& .MuiFormGroup-root': {
      margin: '1.5rem 0 0 1.5rem',
    },
  },
  firstIcon: {
    '&&': {
      margin: '2.25rem auto auto',
    },
  },
  icon: {
    alignSelf: 'baseline',
    margin: '0.5rem auto auto',
  },
});

const normalize = (str?: string) => str?.replace(/\D/g, '') ?? '';
const formatter = new AsYouTypeFormatter('US');
const formatAsPhoneNumber = (str?: string): string => {
  formatter.clear();
  return [...normalize(str)]
    .slice(0, 10)
    .reduce((_total, digit) => formatter.inputDigit(digit), '');
};

type Props = {
  className?: string;
  disabled?: boolean;
  phones: MemberPhone[];
  submitted?: boolean;
  getItemReadOnly?: (phone: MemberPhone) => boolean;
  getItemReadOnlyPrimary?: (phone: MemberPhone) => boolean;
  onChange: (
    phones: MemberPhone[],
    invalidPhone?: MemberPhone,
    deletedPhone?: MemberPhone,
  ) => void;
};

export const EditPhoneGroup = ({
  className,
  disabled,
  phones,
  submitted,
  getItemReadOnly,
  getItemReadOnlyPrimary,
  onChange,
}: Props) => {
  const styles = useStyles();

  const handleChange = (
    phones?: MemberPhoneOptional[],
    invalidPhone?: MemberPhone,
    deletedPhone?: MemberPhone,
  ) =>
    onChange([...(phones || [])] as MemberPhone[], invalidPhone, deletedPhone);

  return (
    <div
      className={classNames(className, styles.container, 'grid-wrapper fit')}
    >
      {[...((phones || []) as MemberPhoneOptional[])].map((phone, idx) => (
        <Fragment key={`edit-phone-${phone.id}-${idx}`}>
          <RadioGroup
            className={classNames(
              idx === 0 && styles.firstRadio,
              styles.radio,
              'grid-span-1',
            )}
            disabled={
              disabled || getItemReadOnlyPrimary?.(phone as MemberPhone)
            }
            items={[{ value: idx + 1, label: '' }]}
            title={
              idx === 0 ? translate('components.editPhones.primary') : undefined
            }
            onChange={() => {
              phones?.forEach((item, phonesIdx) => {
                if (item.phone.type === phone.phone.type) {
                  item.primary = idx === phonesIdx;
                  item.preferred = item.primary && item.preferred;
                }
              });
              handleChange(phones);
            }}
            value={phone.primary ? idx + 1 : -1}
          />
          <TextInput
            className="grid-span-5"
            disabled={disabled || getItemReadOnly?.(phone as MemberPhone)}
            error={
              submitted &&
              (phone.phone?.number
                ? !validate(phone.phone?.number) &&
                  translate('components.editPhones.phoneNumberInvalid')
                : translate('global.missingRequiredFieldMin'))
            }
            label={
              idx === 0 ? (
                <span className="flex middle" style={{ height: '17.14px' }}>
                  {translate('components.editPhones.phone')}{' '}
                  {!disabled && (
                    <button
                      className={classNames('flex middle', styles.addLink)}
                      onClick={(evt) => {
                        evt.preventDefault();
                        handleChange([
                          ...(phones || []),
                          new MemberPhone({ phone: {} }),
                        ]);
                      }}
                    >
                      <AddIcon className={styles.addIcon} fontSize="small" />
                      {translate('global.addAnotherNumber')}
                    </button>
                  )}
                </span>
              ) : undefined
            }
            maxLength={14}
            onChange={(val) => {
              if (val) {
                if (!phone.phone) {
                  phone.phone = new Phone({});
                }
                if (val.length > 1) {
                  phone.phone.number =
                    parse(val)?.getNationalNumber()?.toString() || '';
                  phone.phone.number = [...phone.phone.number]
                    .slice(0, 10)
                    .join('');
                } else {
                  phone.phone.number = val;
                }
              } else {
                delete phone.phone?.number;
              }
              handleChange(phones);
            }}
            value={
              phone.phone?.number
                ? formatAsPhoneNumber(phone.phone?.number)
                : undefined
            }
          />
          <Select
            className="grid-span-5"
            disabled={disabled || getItemReadOnly?.(phone as MemberPhone)}
            disableClearable
            error={
              submitted &&
              !phone.phone?.type &&
              translate('global.missingRequiredFieldMin')
            }
            items={Enum.toSelectable([PhoneType.CELL, PhoneType.HOME])}
            label={
              idx === 0 ? translate('components.editPhones.type') : undefined
            }
            onChange={(val: Selectable) => {
              const type = PhoneType.byKey[val.value];
              if (!phone.phone) {
                phone.phone = new Phone({});
              }
              phone.phone.type = type;
              let primaryFound = false;
              phones.forEach((item) => {
                if (!primaryFound && item.phone.type === type && phone.primary)
                  primaryFound = true;
                else if (item.phone.type === type && phone.primary)
                  item.primary = false;
              });

              handleChange(phones);
            }}
            value={
              phone.phone?.type
                ? Enum.toSelectable([phone.phone?.type])[0]
                : undefined
            }
            required
          />
          <div
            className={classNames(
              idx === 0 && styles.firstIcon,
              styles.icon,
              'grid-span-1 flex gap',
            )}
          >
            {phone.id ? (
              <IconButton
                disabled={
                  (phones && phones.length < 2) ||
                  disabled ||
                  getItemReadOnly?.(phone as MemberPhone)
                }
                size="small"
                onClick={() => {
                  phones?.splice(idx, 1);
                  handleChange(
                    phones,
                    phone?.id ? (phone as MemberPhone) : undefined,
                  );
                }}
                tooltip={translate('memberProfile.phones.invalidate')}
              >
                <CloseIcon fontSize="large" />
              </IconButton>
            ) : (
              <div style={{ width: '4.1rem' }} />
            )}
            <IconButton
              disabled={
                (phones && phones.length < 2) ||
                disabled ||
                getItemReadOnly?.(phone as MemberPhone)
              }
              size="small"
              onClick={() => {
                phones?.splice(idx, 1);
                handleChange(
                  phones,
                  undefined,
                  phone?.id ? (phone as MemberPhone) : undefined,
                );
              }}
              tooltip={translate('memberProfile.phones.delete')}
            >
              <DeleteOutlineIcon fontSize="large" />
            </IconButton>
          </div>
        </Fragment>
      ))}
    </div>
  );
};
